import React, { useEffect, useState } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalProduct.module.sass";
import Icon from "../Icon";
import TextInput from "../TextInput";
import Card from "../Card";
import Dropdown from "../Dropdown";
import { uploadDocumentToFolder } from "../../services/folderDocument.service";
import { showSuccess, showError } from "../../services/toast.service";
import { useAuth } from "../../contexts/AuthContext";


const ModalImportFileToFolder = ({ visible, onClose, folderId, onDocumentUploaded }) => {
  const [fileName, setFileName] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [isGenerationInProgress, setIsGenerationInProgress] = useState(false);
  const [documentType, setDocumentType] = useState("Autre");
  const [documentTitle, setDocumentTitle] = useState("");
  
  const { getPartnerId } = useAuth();

  // Types de documents disponibles pour les dossiers
  const documentTypes = [
    "Facture",
    "Convention", 
    "Contrat",
    "Attestation",
    "Certificat",
    "Autre"
  ];


  // Reset the form when modal is closed
  useEffect(() => {
    if (!visible) {
      setFileName("");
      setFileObj(null);
      setIsGenerationInProgress(false);
      setDocumentType("Autre");
      setDocumentTitle("");
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      const target = document.querySelector("#modal-product");
      disableBodyScroll(target);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [visible]);

  const handleFileChange = (e, type) => {
    const files = e.target.files;
    if (!files.length) return;
    
    setFileObj(files[0]);
    if (!fileName) {
      setFileName(files[0].name.split('.').slice(0, -1).join('.'));
    }
  };
  
  const handleImportFile = async () => {
    if (!fileName || !fileObj || !folderId) return;

    try {
      setIsGenerationInProgress(true);
      
      // Récupérer le partner_id de l'utilisateur connecté
      const partnerId = getPartnerId();
      if (!partnerId) {
        showError("Impossible de récupérer les informations du partenaire");
        return;
      }
      
      console.log("Importation du fichier :", fileName, fileObj, "pour le dossier ID:", folderId);
      
      // Utiliser le service uploadDocumentToFolder avec tous les paramètres requis
      const result = await uploadDocumentToFolder(
        folderId, 
        fileObj, 
        partnerId, 
        documentTitle || fileName, 
        documentType
      );
      
      if (result) {
        console.log("Document uploadé avec succès:", result);
        showSuccess("Document importé avec succès");
        
        // Si la fonction onDocumentUploaded est fournie, l'appeler pour rafraîchir la liste
        if (typeof onDocumentUploaded === 'function') {
          onDocumentUploaded();
        }
        
        // Fermer la modale
        onClose();
      } else {
        console.error("Échec de l'upload du document");
        showError("Échec de l'upload du document. Veuillez réessayer.");
      }
    } catch (error) {
      console.error("Erreur lors de l'importation du fichier :", error);
      showError("Erreur lors de l'upload du document: " + (error.message || "Erreur inconnue"));
    } finally {
      setIsGenerationInProgress(false);
    }
  };

  return createPortal(
    visible && (
      <div id="modal-product" className={styles.modal}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose}>
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer} style={{ position: 'relative' }}>
          <Card
            className={cn(styles.card)}
            title="Importer un document"
            classTitle="title-green"
            style={{ minHeight: '600px', position: 'relative' }}
          >
            <div className={styles.description}>
              <div className={styles.field} style={{ position: 'relative', zIndex: 9999 }}>

                <TextInput
                  className={styles.field}
                  label="Titre du document"
                  name="documentTitle"
                  id="documentTitle"
                  type="text"
                  value={documentTitle}
                  onChange={(e) => {
                    const titleValue = typeof e === 'object' && e.target ? e.target.value : e;
                    setDocumentTitle(titleValue);
                  }}
                />
                              
                <Dropdown
                  className={styles.field}
                  label="Type de document"
                  options={documentTypes}
                  value={documentType}
                  setValue={setDocumentType}
                />
                
                <div style={{ height: '24px' }}></div>

                <div className={styles.uploadZone}>
                  <input
                    type="file"
                    className={styles.fileInput}
                    onChange={(e) => handleFileChange(e, documentType)}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <span className={styles.uploadText}>
                    Glissez un fichier ici ou cliquez pour parcourir
                  </span>
                </div>

                {fileObj && (
                  <div className={styles.fileList}>
                    <div className={styles.fileItem}>
                      <div className={styles.fileInfo}>
                        <span className={styles.fileName}>
                          {fileObj.name.split('.').slice(0, -1).join('.')}
                          <span className={styles.fileExt}>.{fileObj.name.split('.').pop()}</span>
                        </span>
                      </div>
                      <button
                        className={styles.removeButton}
                        onClick={() => setFileObj(null)}
                        title="Supprimer"
                      >
                        <Icon name="close" size="20" />
                      </button>
                    </div>
                  </div>
                )}

              </div>
              <div className={styles.btnWrap}>
                <button
                  className="button-green"
                  onClick={handleImportFile}
                  disabled={!documentTitle || !fileName || !fileObj}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isGenerationInProgress ? (
                    <>
                      <Icon name="download" size="16" />
                      Importation en cours...
                    </>
                  ) : (
                    <>
                      <Icon name="download" size="16" />
                      Importer
                    </>
                  )}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    ),
    document.body
  );
};

export default ModalImportFileToFolder;
